import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BotonAcceso from "../elementos/BotonAcceso.jsx";
import { useTheme } from "../guardarToken/themeContext";
import { Building2, Save, ArrowLeft } from 'lucide-react';

const apiUrlSectores = process.env.REACT_APP_API_SECTORES || '/api/sectores/section/';
const apiUrlDirecciones = process.env.REACT_APP_API_DIRECCIONES || '/api/direcciones/addres/';
const apiUrlSedes = process.env.REACT_APP_API_SEDES || '/api/sedes/';

const CreateSection = () => {
    const [sector, setSector] = useState('');
    const [sede_id, setSedeId] = useState('');
    const [id_direccion, setIdDireccion] = useState('');
    const [direcciones, setDirecciones] = useState([]);
    const [sedes, setSedes] = useState([]);
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [sedesRes, diresRes] = await Promise.all([
                    axios.get(apiUrlSedes),
                    axios.get(apiUrlDirecciones)
                ]);
                setSedes(sedesRes.data);
                setDirecciones(diresRes.data);
            } catch (error) {
                console.error('Error al cargar datos iniciales:', error);
            }
        };
        loadInitialData();
    }, []);

    // Direcciones filtradas por la sede seleccionada
    const direccionesFiltradas = direcciones.filter(dir =>
        !sede_id || dir.sede_id === sede_id
    );

    const store = async (e) => {
        e.preventDefault();
        try {
            await axios.post(apiUrlSectores, {
                sector,
                sede_id: sede_id || null,
                id_direccion: id_direccion || null
            });
            navigate('/admin/AbmSectores');
        } catch (error) {
            console.error('Error al guardar el sector:', error);
        }
    };

    return (
        <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
            <div className={`w-full max-w-2xl ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-100"} rounded-3xl shadow-2xl border p-8 animate-in zoom-in duration-300`}>

                <div className="flex flex-col items-center mb-8">
                    <div className="bg-gradient-to-br from-orange-500 to-orange-700 p-4 rounded-2xl shadow-lg shadow-orange-900/30 mb-4">
                        <Building2 className="text-white w-8 h-8" />
                    </div>
                    <h2 className="text-2xl font-black tracking-tight mb-1">Nuevo Sector</h2>
                    <p className={`text-xs font-bold uppercase tracking-widest ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Crear departamento dentro de una sede</p>
                </div>

                <form className="space-y-6" onSubmit={store}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Nombre del Sector</label>
                            <input
                                type="text"
                                placeholder="Ej: Sistemas / Administración"
                                value={sector}
                                onChange={(e) => setSector(e.target.value)}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"
                                    } border`}
                                required
                            />
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Sede Principal</label>
                            <select
                                value={sede_id}
                                onChange={(e) => {
                                    setSedeId(e.target.value);
                                    setIdDireccion(''); // Limpiar dirección al cambiar sede
                                }}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"
                                    } border`}
                                required
                            >
                                <option value="">Seleccione una sede</option>
                                {sedes.map(s => (
                                    <option key={s.id} value={s.id}>{s.nombre}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Dirección Específica (Opcional)</label>
                            <select
                                value={id_direccion}
                                onChange={(e) => setIdDireccion(e.target.value)}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"
                                    } border`}
                            >
                                <option value="">Sin dirección específica</option>
                                {direccionesFiltradas.map(dir => (
                                    <option key={dir.id_direccion} value={dir.id_direccion}>
                                        {dir.direccion} ({dir.localidad})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex gap-4 pt-6">
                        <BotonAcceso
                            variant="accent"
                            type='submit'
                            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-black text-xs uppercase tracking-widest h-14 rounded-2xl shadow-xl shadow-orange-900/20 flex items-center justify-center gap-2 transform active:scale-95 transition-all"
                        >
                            <Save className="w-4 h-4" /> Guardar Sector
                        </BotonAcceso>
                        <BotonAcceso
                            variant="dark"
                            type='button'
                            onClick={() => navigate('/admin/AbmSectores')}
                            className={`px-8 ${isDarkMode ? "bg-slate-800 hover:bg-slate-700" : "bg-slate-100 hover:bg-slate-200 text-slate-600"} font-black text-xs uppercase tracking-widest h-14 rounded-2xl flex items-center justify-center gap-2 transition-all`}
                        >
                            <ArrowLeft className="w-4 h-4" /> Volver
                        </BotonAcceso>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateSection;
